import {animate, state, style, transition, trigger} from '@angular/animations';
import {CommonModule} from '@angular/common';
import {Component, computed, inject} from '@angular/core';
import {ClinicService} from '@infrastructure/admin/clinics/clinic.service';
import {GenericAuthService} from '@infrastructure/generic-auth/generic-auth.service';
import {UserType} from '@infrastructure/generic-auth/user-type.enum';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {AngularSvgIconModule} from 'angular-svg-icon';
import {MenuItem} from 'src/domain/sidebar';
import {MenuItemComponent} from '../menu-item/menu-item.component';

@Component({
    selector: 'app-sidebar',
    standalone: true,
    imports: [AngularSvgIconModule, MenuItemComponent, TranslateModule, CommonModule],
    templateUrl: './sidebar.component.html',
    styleUrl: './sidebar.component.scss',
    animations: [
        trigger('activeLanguageState', [
            state('true', style({ left: '2px' })),
            state('false', style({ left: 'calc(50% + 2px)' })),
            transition('true <=> false', animate('100ms'))
        ])
    ]
})
export class SidebarComponent
{
    private readonly clinicService = inject(ClinicService);

    private readonly translate = inject(TranslateService);

    private readonly auth = inject(GenericAuthService);

    private readonly showProfessionals = computed(() => this.auth.loggedUserType() !== UserType.ADMIN || this.clinicService.total() > 0);

    public readonly langs = ['pt', 'en'];

    private readonly menuItems: MenuItem[] = [
        {label: 'SIDEBAR.CLINICS', value: 'clinics', icon: 'hospital', permission: [UserType.OWNER, UserType.ADMIN]},
        {label: 'SIDEBAR.HEALTHCARE_PROFESSIONALS', value: 'healthcare-professionals', icon: 'badge', permission: [UserType.OWNER, UserType.ADMIN]},
        {label: 'SIDEBAR.AGENDA', value: 'agenda', icon: 'calendar', permission: [UserType.CLINICIAN]},
        {label: 'SIDEBAR.PATIENTS', value: 'patients', icon: 'people', permission: [UserType.OWNER, UserType.CLINICIAN]},
        {label: 'SIDEBAR.CAMPAIGNS', value: 'campaigns', icon: 'megaphone', permission: [UserType.OWNER]},
        {label: 'SIDEBAR.SPECIALTIES', value: 'specialities', icon: 'heart', permission: [UserType.OWNER]},
        {label: 'SIDEBAR.LOCATIONS', value: 'locations', icon: 'world', permission: [UserType.OWNER]}
    ];

    private readonly bottomMenuItems: MenuItem[] = [
        {label: 'SIDEBAR.CONFIGURATIONS', value: 'configurations', icon: 'config', permission: [UserType.ADMIN, UserType.CLINICIAN]},
        {label: 'SIDEBAR.SUPPORT', value: 'support', icon: 'lifebuoy', permission: [UserType.ADMIN, UserType.CLINICIAN]}
    ];

    public readonly userTypeMenuItems = computed(() =>
    {
        return this.menuItems
            .filter((item) =>
            {
                const hasPermission = item.permission.includes(this.auth.loggedUserType());
                const isVisible = item.value !== 'healthcare-professionals' || this.showProfessionals();

                return hasPermission && isVisible;
            });
    });

    public readonly userTypeBottomMenuItems = computed(() =>
    {
        return this.bottomMenuItems.filter((item) => item.permission.includes(this.auth.loggedUserType()));
    });

    public isActiveLanguage(lang: string): boolean
    {
        return this.translate.currentLang === lang;
    }

    public toggleLanguage(): void
    {
        const language = this.translate.currentLang === 'en' ? 'pt' : 'en';
        this.translate.use(language);
        localStorage.setItem('language', language);
    }
}
